import React, { useRef } from "react";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";

import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

import { setPrevState } from "../../../../util/common";
import { regexName } from "../../../../util/regularExpress";

const Types = [{ value: "email", text: "Email" }];

const AddJobForm = (props) => {
  const { jobState, setJobState, dataPipelineList, confirmPipelineChange, Job_id } = props;

  const textRef = useRef(null);

  /**
   * Alpha Numeric Characters only with Space allow (first letter should Alpha)
   * @param {*} str
   * @returns
   */
  const handleJobName = (str, dataState) => {
    const isValid = regexName(str, "jobName", "_ -");
    if (isValid) setPrevState(setJobState, { [dataState]: str });
  };

  return (
    <Grid container direction={"row"} spacing={2}>
      <Grid>
        <TextField
          value={jobState.name}
          sx={{ width: { xs: "100%", sm: "300px" } }}
          required
          label="Job Name"
          inputProps={{ maxLength: 30 }}
          onChange={(e) => handleJobName(e.target.value, "name")}
        />
      </Grid>
      <Grid>
        <FormControl sx={{ width: { xs: "100%", sm: "300px" } }} required>
          <InputLabel>Pipeline</InputLabel>
          <Select
            value={jobState.DataPipeline_id}
            onChange={confirmPipelineChange}
            label="Pipeline"
            disabled={!!Job_id}
            MenuProps={{ PaperProps: { style: { maxHeight: "300px", maxWidth: "300px" } } }}
            renderValue={(selected) => {
              const selectedPipeline = dataPipelineList.find((p) => p.DataPipeline_id === selected);
              const isOverflow = (textRef.current?.scrollWidth > textRef.current?.clientWidth || false) && !!Job_id;

              return (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Tooltip placement="bottom" title={selectedPipeline?.Name} arrow disableHoverListener={!isOverflow}>
                    <Typography
                      variant="body1"
                      ref={textRef}
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}
                    >
                      {selectedPipeline?.Name || "Select a pipeline"}
                    </Typography>
                  </Tooltip>
                  {selectedPipeline?.IsDefault === 1 && (
                    <Tooltip placement="right" title="Default Pipeline" arrow>
                      <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                    </Tooltip>
                  )}
                </Stack>
              );
            }}
          >
            {dataPipelineList.map((p) => (
              <MenuItem
                key={p.DataPipeline_id}
                value={p.DataPipeline_id}
                style={{ whiteSpace: "normal", wordWrap: "break-word", wordBreak: "break-all" }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography variant="body1" sx={{ m: 0, p: 0 }}>
                    {p.Name}
                  </Typography>
                  {p.IsDefault === 1 && (
                    <Tooltip placement="right" title="Default Pipeline" arrow>
                      <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                    </Tooltip>
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid>
        <FormGroup>
          <FormControlLabel
            control={<Checkbox />}
            value={jobState.isEntryJob}
            checked={jobState.isEntryJob}
            onChange={() => setJobState((prev) => ({ ...prev, isEntryJob: !prev.isEntryJob }))}
            label="Set as the entry point for the pipeline"
          />
        </FormGroup>
      </Grid>
      <Grid spacing={2}>
        <FormControl sx={{ width: { xs: "100%", sm: "300px" } }}>
          <InputLabel required>Type</InputLabel>
          <Select
            value={jobState.Type ?? "email"}
            label="Type"
            required
            MenuProps={{ sx: { height: "220px" } }}
            onChange={(e) => {
              setPrevState(setJobState, { Type: e.target.value ?? "email" });
            }}
          >
            {Types.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
};

AddJobForm.propTypes = {
  jobState: PropTypes.object.isRequired,
  dataPipelineList: PropTypes.array.isRequired,
  setJobState: PropTypes.func.isRequired,
  confirmPipelineChange: PropTypes.func.isRequired,
  Job_id: PropTypes.string.isRequired,
};

export default React.memo(AddJobForm);
